<template>
    <!-- Posts Grid -->
    <section class="py-12">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center max-w-3xl mx-auto mb-16">
                <h2 class="text-3xl sm:text-4xl mb-4">
                    {{ $t('blog.title') }}
                </h2>
                <p class="text-lg text-muted-foreground">
                    {{ $t('blog.description') }}
                </p>
            </div>
            <div
                v-if="posts.length > 0"
                class="grid md:grid-cols-2 gap-8"
            >
                <article
                    v-for="(post, index) in posts"
                    :key="post.slug"
                    class="blog-card group"
                    :class="index === 0 ? 'md:col-span-2' : ''"
                    :style="{ animationDelay: `${index * 100}ms` }"
                >
                    <router-link
                        v-if="post.image"
                        :to="`/${currentLocale}/blog/${post.slug}`"
                        class="relative block overflow-hidden bg-white"
                        :class="index === 0 ? 'h-64 md:h-80' : 'h-48'"
                    >
                        <img
                            :src="post.image"
                            :alt="post.title"
                            class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                        >
                        <time
                            :datetime="post.publishedAt"
                            class="absolute bottom-3 left-6 z-10 px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full tracking-wide uppercase"
                        >
                            {{ formatDate(post.publishedAt) }}
                        </time>
                    </router-link>
                    <div :class="post.image ? 'px-6 pb-6 pt-3' : 'p-6'">
                        <div v-if="!post.image" class="flex items-center gap-3 mb-4">
                            <time
                                :datetime="post.publishedAt"
                                class="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full tracking-wide uppercase"
                            >
                                {{ formatDate(post.publishedAt) }}
                            </time>
                        </div>
                        <h2
                            class="font-semibold text-foreground mb-3"
                            :class="index === 0 ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'"
                        >
                            <router-link
                                :to="`/${currentLocale}/blog/${post.slug}`"
                                class="hover:text-accent transition-colors"
                            >
                                {{ post.title }}
                            </router-link>
                        </h2>
                        <p class="text-muted-foreground mb-5 leading-relaxed">
                            {{ post.summary }}
                        </p>
                        <router-link
                            :to="`/${currentLocale}/blog/${post.slug}`"
                            class="inline-flex items-center gap-1.5 text-accent font-medium hover:gap-2.5 transition-all"
                        >
                            {{ $t('blog.readMore') }}
                            <ArrowRightIcon class="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </router-link>
                    </div>
                </article>
            </div>

            <div
                v-else
                class="text-center py-16 text-muted-foreground"
            >
                {{ $t('blog.noPosts') }}
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useRoute } from 'vue-router';
import { getBlogPosts } from '@/composables/useBlogContent';
import { useBlogListSeoHead } from '@/composables/useBlogSeoHead';
import { ArrowRightIcon } from '@heroicons/vue/24/outline';

export default defineComponent({
    name: 'BlogList',
    components: {
        ArrowRightIcon,
    },
    setup() {
        const route = useRoute();

        const currentLocale = computed(() => {
            const routeLocale = route.params.locale as string;
            return (routeLocale === 'en' || routeLocale === 'lt') ? routeLocale : 'en';
        });

        const posts = computed(() => getBlogPosts(currentLocale.value));

        useBlogListSeoHead();

        const formatDate = (dateStr: string) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString(currentLocale.value === 'lt' ? 'lt-LT' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        };

        return {
            posts,
            currentLocale,
            formatDate,
        };
    },
});
</script>

<style scoped>
.blog-card {
    @apply relative rounded-2xl overflow-hidden border border-border bg-white;
    @apply transition-all duration-300;
    animation: slideUp 0.5s ease-out both;
}

.blog-card::before {
    content: '';
    @apply absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-accent opacity-0 transition-opacity duration-300;
    z-index: 1;
}

.blog-card:hover {
    @apply -translate-y-1 shadow-xl border-accent/20;
}

.blog-card:hover::before {
    @apply opacity-100;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
